import React, { useState, useEffect, useRef } from 'react';
import DataTableCrudTest from './DataTableCrudTest';
import DataTreeCrudTest from './DataTreeCrudTest';
import CardGridTest from './CardGridTest';
import SidebarDocumentTest from './SidebarDocumentTest';
import LayoutVariationsDemo from './LayoutVariationsDemo';
import TestHeader from './TestHeader';
import TestMenu from './TestMenu';
import TestFooter from './TestFooter';
import TestModuleHeader from './TestModuleHeader';
import TestTabs from './TestTabs';
import TestRightSidebar from './TestRightSidebar';
import DataTableDetailModal from './DataTableDetailModal';
import GenericAddModal from './GenericAddModal';
import ModalPortal from './ModalPortal';
import TaskWidget from './widgets/TaskWidget';

interface ComponentOption {
  id: string;
  name: string;
  description: string;
  component: React.ComponentType;
  cssFile: string;
}

const ComponentDemo: React.FC = () => {
  const [selectedComponent, setSelectedComponent] = useState<string>('data-table');
  const [showCSSDetails, setShowCSSDetails] = useState(false);
  const [cssContent, setCssContent] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const componentOptions: ComponentOption[] = [
    {
      id: 'data-table-crud',
      name: 'Data Table CRUD',
      description: 'Interactive data table with CRUD operations, sorting, and pagination',
      component: DataTableCrudTest,
      cssFile: 'data-table-crud.md'
    },
    {
      id: 'data-tree-crud',
      name: 'Hierarchical Tree CRUD',
      description: 'Expandable tree structure with CRUD operations and nested data',
      component: DataTreeCrudTest,
      cssFile: 'data-tree-crud.md'
    },
    {
      id: 'card-grid',
      name: 'Card Grid Layout',
      description: 'Responsive card grid with hover effects and actions',
      component: CardGridTest,
      cssFile: 'card-grid-layout.md'
    },
    {
      id: 'sidebar-document',
      name: 'Sidebar + Document',
      description: 'Split layout with collapsible sidebar and main content area',
      component: SidebarDocumentTest,
      cssFile: 'sidebar-document.md'
    },
    {
      id: 'layout-variations',
      name: 'Layout Variations Demo',
      description: 'Interactive demonstration of different layout configurations',
      component: LayoutVariationsDemo,
      cssFile: 'layout-variations.md'
    },
    {
      id: 'test-header',
      name: 'Test Header',
      description: 'Application header component with navigation and branding',
      component: () => <TestHeader title="Component Demo Header" onBackToApp={() => {}} onShowReference={() => {}} />,
      cssFile: 'test-header.md'
    },
    {
      id: 'test-menu',
      name: 'Test Menu',
      description: 'Vertical navigation menu component',
      component: TestMenu,
      cssFile: 'test-menu.md'
    },
    {
      id: 'test-footer',
      name: 'Test Footer',
      description: 'Application footer with branding and links',
      component: () => <TestFooter appName="AnyGRC" version="v2.0" />,
      cssFile: 'test-footer.md'
    },
    {
      id: 'test-module-header',
      name: 'Test Module Header',
      description: 'Module header with title and action buttons',
      component: () => <TestModuleHeader title="Sample Module" onSettingsClick={() => {}} />,
      cssFile: 'test-module-header.md'
    },
    {
      id: 'test-tabs',
      name: 'Test Tabs',
      description: 'Tab navigation component with multiple tab support',
      component: () => <TestTabs activeTab="tab1" onTabChange={() => {}} />,
      cssFile: 'test-tabs.md'
    },
    {
      id: 'test-right-sidebar',
      name: 'Test Right Sidebar',
      description: 'Right sidebar widget area with various content panels',
      component: () => <TestRightSidebar activeTab="tab1" />,
      cssFile: 'test-right-sidebar.md'
    },
    {
      id: 'data-table-detail-modal',
      name: 'Data Table Detail Modal',
      description: 'Modal dialog for viewing/editing data table item details',
      component: () => <DataTableDetailModal
        isOpen={true}
        onClose={() => {}}
        selectedUser={{ id: 1, name: 'Sample User', email: 'user@example.com', role: 'Admin', status: 'Active', created: '2025-01-01' }}
      />,
      cssFile: 'data-table-detail-modal.md'
    },
    {
      id: 'generic-add-modal',
      name: 'Generic Add Modal',
      description: 'Generic modal for adding new items with form validation',
      component: () => <GenericAddModal
        isOpen={true}
        onClose={() => {}}
        onSave={(data) => console.log('Saved:', data)}
        title="Add New Item"
      />,
      cssFile: 'generic-add-modal.md'
    },
    {
      id: 'modal-portal',
      name: 'Modal Portal',
      description: 'Portal component for rendering modals outside normal DOM hierarchy',
      component: () => <ModalPortal isOpen={true} onClose={() => {}}>
        <div style={{ padding: '20px', background: 'white', borderRadius: '8px' }}>
          <h3>Modal Content</h3>
          <p>This modal is rendered through a portal.</p>
        </div>
      </ModalPortal>,
      cssFile: 'modal-portal.md'
    },
    {
      id: 'task-widget',
      name: 'Task Widget',
      description: 'Task management widget with priority indicators and due dates',
      component: () => <TaskWidget
        tasks={[
          { id: '1', title: 'Complete project review', priority: 'high', dueDate: '2025-10-10', completed: false },
          { id: '2', title: 'Update documentation', priority: 'medium', dueDate: '2025-10-15', completed: true }
        ]}
        onTaskToggle={() => {}}
      />,
      cssFile: 'task-widget.md'
    }
  ];

  const selectedOption = componentOptions.find(opt => opt.id === selectedComponent);
  const SelectedComponent = selectedOption?.component;

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load CSS content when component changes
  useEffect(() => {
    if (showCSSDetails && selectedOption) {
      // Load CSS content from MD file
      const loadCSSContent = async () => {
        try {
          const response = await fetch(`/docs/css-reference/${selectedOption.cssFile}`);
          if (response.ok) {
            const content = await response.text();
            setCssContent(content);
          } else {
            // Fallback to sample content if file not found
            setCssContent(`# ${selectedOption.name} - CSS Technical Reference

CSS documentation file not found: \`${selectedOption.cssFile}\`

Please create the file at \`docs/css-reference/${selectedOption.cssFile}\` with technical details.

## Component Structure
\`\`\`tsx
<${selectedOption.name.replace(/\s+/g, '')} />
\`\`\`

## Description
${selectedOption.description}

## CSS Classes Used
- Component-specific classes to be documented
- Layout and interaction classes
- Visual styling classes

## Implementation Notes
- Add technical details here
- Include responsive behavior
- Document accessibility features
- Note performance considerations`);
          }
        } catch (error) {
          console.error('Error loading CSS documentation:', error);
          setCssContent(`# Error Loading CSS Documentation

Failed to load \`${selectedOption.cssFile}\`

${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      };

      loadCSSContent();
    }
  }, [selectedComponent, showCSSDetails, selectedOption]);

  return (
    <div className="component-demo-container">
      {/* Controls Row */}
      <div className="container-toolbar" style={{ marginBottom: '16px' }}>
        <div className="layout-flex-row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>

          {/* Component Selector */}
          <div className="layout-flex-row" style={{ alignItems: 'center', gap: '12px' }}>
            <label style={{ fontWeight: '500', color: '#374151' }}>
              Select Component:
            </label>
            <div ref={dropdownRef} style={{ position: 'relative', minWidth: '250px' }}>
              {/* Dropdown Button */}
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: '#ffffff',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedOption?.name || 'Select Component'}
                </span>
                <span style={{
                  marginLeft: '8px',
                  transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}>
                  ▼
                </span>
              </button>

              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000
                  }}
                >
                  {componentOptions.map(option => (
                    <button
                      key={option.id}
                      onClick={() => {
                        setSelectedComponent(option.id);
                        setDropdownOpen(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: 'none',
                        background: selectedComponent === option.id ? '#f3f4f6' : '#ffffff',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f9fafb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = selectedComponent === option.id ? '#f3f4f6' : '#ffffff';
                      }}
                    >
                      <div style={{ fontWeight: '500', color: '#1f2937' }}>
                        {option.name}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* CSS Details Button */}
          <button
            onClick={() => setShowCSSDetails(true)}
            disabled={!selectedOption}
            style={{
              padding: '8px 16px',
              background: selectedOption ? '#4f46e5' : '#9ca3af',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: selectedOption ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.2s',
              opacity: selectedOption ? 1 : 0.6
            }}
            onMouseEnter={(e) => {
              if (selectedOption) {
                e.currentTarget.style.background = '#4338ca';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedOption) {
                e.currentTarget.style.background = '#4f46e5';
              }
            }}
          >
            Show CSS Details
          </button>
        </div>

        {/* Component Description - REMOVED to save screen space */}
        {/* {selectedOption && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            background: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              {selectedOption.description}
            </div>
          </div>
        )} */}
      </div>

      {/* Main Content Area - Full Width */}
      <div className="layout-flex-row" style={{
        gap: '16px',
        height: 'calc(100vh - 200px)', // Increased available space
        minHeight: '400px'
      }}>

        {/* Component Showcase Area - Full Width */}
        <div
          className="component-showcase-area"
          style={{
            flex: '1',
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '16px',
            overflow: 'auto',
            minHeight: '400px',
            maxHeight: 'calc(100vh - 200px)',
            position: 'relative'
          }}
        >
          {SelectedComponent ? (
            <SelectedComponent />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: '300px',
              color: '#6b7280',
              fontSize: '16px',
              fontWeight: '500'
            }}>
              No component is selected
            </div>
          )}
        </div>

        {/* CSS Details Sidebar - REMOVED, now shown in modal */}
      </div>

      {/* CSS Technical Details Modal */}
      <ModalPortal isOpen={showCSSDetails && !!selectedOption} onClose={() => setShowCSSDetails(false)} size="large">
        {/* Modal Header */}
        <div className="modal-header">
          <div>
            <h2>CSS Technical Reference</h2>
            <div className="text-code-mono" style={{ fontSize: '12px', color: '#e9d5ff' }}>
              {selectedOption?.cssFile || 'No component selected'}
            </div>
          </div>
          <button
            className="modal-close-button"
            onClick={() => setShowCSSDetails(false)}
          >
            ×
          </button>
        </div>

        {/* Modal Content - Scrollable */}
        <div className="modal-body">
          <div style={{
            fontSize: '13px',
            lineHeight: '1.5',
            color: '#374151',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap'
          }}>
            {selectedOption ? cssContent : 'Please select a component first to view its CSS technical reference.'}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-actions">
          <button
            className="modal-action-btn btn-secondary"
            onClick={() => setShowCSSDetails(false)}
          >
            Close
          </button>
        </div>
      </ModalPortal>


    </div>
  );
};

export default ComponentDemo;